/**
 * Cognito authentication for the campaign dashboard.
 *
 * This dashboard was public: App.tsx routed straight to AdminDashboard with no
 * guard, so anyone with the URL could start campaigns, block domains and
 * suppress contacts against 124k real contacts. The shared API token added in
 * 918dcb4 stopped scripted access to the API but not a person opening the
 * dashboard, because their browser is handed the token. This closes that.
 *
 * Authorization-code flow with PKCE against the angelwatch-campaign-admin pool.
 * No client secret: there is nowhere in a browser to keep one. Sign-up is
 * disabled on the pool (AllowAdminCreateUserOnly), so accounts are created by an
 * administrator and cannot be self-registered.
 *
 * oidc-client-ts rather than hand-rolled PKCE - state and nonce validation,
 * token refresh and storage are easy to get subtly wrong, and being wrong here
 * means an auth bypass.
 */

import { UserManager, WebStorageStateStore, User } from 'oidc-client-ts';

const REGION = 'us-east-1';
const USER_POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID || 'us-east-1_eQcSYV1lW';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || 'gn1f2pl9dn9if2vmv7qjtb8ju';
const HOSTED_UI = process.env.REACT_APP_COGNITO_DOMAIN || 'angelwatch-campaign-admin';

/** Group a user must be in to reach the dashboard. */
export const REQUIRED_GROUP = 'campaign-admin';

const origin = typeof window !== 'undefined' ? window.location.origin : '';

export const userManager = new UserManager({
  authority: `https://cognito-idp.${REGION}.amazonaws.com/${USER_POOL_ID}`,
  metadataUrl: `https://cognito-idp.${REGION}.amazonaws.com/${USER_POOL_ID}/.well-known/openid-configuration`,
  client_id: CLIENT_ID,
  redirect_uri: `${origin}/auth/callback`,
  post_logout_redirect_uri: `${origin}/`,
  response_type: 'code',
  scope: 'openid email profile',

  // Tokens in sessionStorage, not localStorage: they die with the tab rather
  // than persisting on a shared machine.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),

  // Refresh in the background so a session does not expire mid-campaign.
  automaticSilentRenew: true,
  monitorSession: false
});

/** Cognito's hosted UI logout is not part of OIDC discovery, so build it. */
export function hostedUiLogoutUrl(): string {
  const base = `https://${HOSTED_UI}.auth.${REGION}.amazoncognito.com/logout`;
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: `${origin}/`
  });
  return `${base}?${params.toString()}`;
}

/** Groups from the ID token. Cognito puts them in cognito:groups. */
export function groupsOf(user: User | null | undefined): string[] {
  const claims = user?.profile as Record<string, unknown> | undefined;
  const raw = claims?.['cognito:groups'];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') return [raw];
  return [];
}

export function isAuthorised(user: User | null | undefined): boolean {
  if (!user || user.expired) return false;
  return groupsOf(user).includes(REQUIRED_GROUP);
}

/**
 * The ID token for API calls. Returns null rather than throwing so a caller can
 * decide what to do; campaignFetch treats null as "send nothing and let the
 * server 401".
 */
export async function getIdToken(): Promise<string | null> {
  try {
    const user = await userManager.getUser();
    if (!user || user.expired) return null;
    return user.id_token ?? null;
  } catch {
    return null;
  }
}

export async function signIn(): Promise<void> {
  await userManager.signinRedirect();
}

export async function signOut(): Promise<void> {
  try {
    await userManager.removeUser();
  } finally {
    // Clear the Cognito session too, otherwise the hosted UI signs straight
    // back in without prompting.
    window.location.href = hostedUiLogoutUrl();
  }
}
